/**
 * Grouping for the two-level category picker, written to survive a backend
 * that has not grown the second level yet.
 *
 * Wallet's categories are `Snacks` under `Food & Drinks`. AIM's `Category`
 * model has had `parentId` since R3 but nothing populates it, so
 * `GET /categories` currently answers with a flat list of parents. Both shapes
 * go through the same function: when no row has a `parentId`, `groupCategories`
 * returns one group per category with no children, and the picker renders that
 * as a plain list. No feature flag, no second code path to keep alive.
 */
import type { EntryCategory } from "./types";

export interface CategoryGroup {
  /** The parent row, or null for the synthetic "Other" bucket (see below). */
  parent: EntryCategory | null;
  children: EntryCategory[];
}

const bySort = (a: EntryCategory, b: EntryCategory) =>
  a.sortOrder - b.sortOrder || a.name.localeCompare(b.name);

/**
 * Parent → children, sorted, with two deliberate behaviours:
 *
 *  * A parent is SELECTABLE in its own right (`Food & Drinks` is a real
 *    category with real records), so the picker renders the parent row as a
 *    tap target and the children indented beneath it.
 *  * A child whose parent is missing from the list — filtered out by a search,
 *    or archived — is not dropped. It lands in a trailing group with a null
 *    parent, because a category you cannot see is a category you cannot pick,
 *    and silently hiding one is how a record ends up in the wrong bucket.
 */
export function groupCategories(categories: EntryCategory[]): CategoryGroup[] {
  const byId = new Map(categories.map((c) => [c.id, c]));
  const parents = categories.filter((c) => !c.parentId).sort(bySort);

  const childrenByParent = new Map<string, EntryCategory[]>();
  const orphans: EntryCategory[] = [];
  for (const c of categories) {
    if (!c.parentId) continue;
    if (!byId.has(c.parentId)) {
      orphans.push(c);
      continue;
    }
    const list = childrenByParent.get(c.parentId) ?? [];
    list.push(c);
    childrenByParent.set(c.parentId, list);
  }

  const groups: CategoryGroup[] = parents.map((p) => ({
    parent: p,
    children: (childrenByParent.get(p.id) ?? []).sort(bySort),
  }));

  if (orphans.length) groups.push({ parent: null, children: orphans.sort(bySort) });
  return groups;
}

/** True when nothing in the list has a parent — the flat, degrade-to-list case. */
export function isFlat(categories: EntryCategory[]): boolean {
  return categories.every((c) => !c.parentId);
}

/**
 * Case-insensitive search that keeps a parent whenever ITS OWN name matches or
 * one of its children does, so a match on `Snacks` still renders under the
 * `Food & Drinks` heading rather than floating loose.
 */
export function filterCategories(categories: EntryCategory[], query: string): EntryCategory[] {
  const q = query.trim().toLowerCase();
  if (!q) return categories;

  const hit = (c: EntryCategory) => c.name.toLowerCase().includes(q);
  const parentsOfHits = new Set(
    categories.filter((c) => c.parentId && hit(c)).map((c) => c.parentId as string),
  );
  return categories.filter((c) => hit(c) || parentsOfHits.has(c.id));
}

/**
 * The archived rule, applied at the OFFER boundary: an archived category is
 * never offered for a new record, EXCEPT when it is the one this record
 * already has. Archiving narrows what is offered going forward; it never
 * rewrites or hides history, and it must not silently clear a saved value.
 */
export function selectableCategories(
  categories: EntryCategory[],
  selectedId: string | null,
): EntryCategory[] {
  return categories.filter((c) => !c.isArchived || c.id === selectedId);
}

/** `Food & Drinks › Snacks`, or just the name when the row has no parent. */
export function categoryPath(
  category: EntryCategory | null | undefined,
  all: EntryCategory[],
): string {
  if (!category) return "";
  if (!category.parentId) return category.name;
  const parent = all.find((c) => c.id === category.parentId);
  return parent ? `${parent.name} › ${category.name}` : category.name;
}
